import { isNumeric } from '@/common/utils/number';

export const SECONDS_IN_MINUTE = 60;
export const SECONDS_IN_HOUR = 60 * SECONDS_IN_MINUTE;
export const SECONDS_IN_WORK_DAY = 8 * SECONDS_IN_HOUR;
export const SECONDS_IN_WORK_WEEK = 5 * SECONDS_IN_WORK_DAY;
export const SECONDS_IN_WORK_MONTH = 21 * SECONDS_IN_WORK_DAY;
export const SECONDS_IN_WORK_QUARTER = 64 * SECONDS_IN_WORK_DAY;
export const SECONDS_IN_WORK_YEAR = 260 * SECONDS_IN_WORK_DAY;

export const UNITS_TO_SECONDS = {
  minute: SECONDS_IN_MINUTE,
  hour: SECONDS_IN_HOUR,
  day: SECONDS_IN_WORK_DAY,
  week: SECONDS_IN_WORK_WEEK,
  month: SECONDS_IN_WORK_MONTH,
  quarter: SECONDS_IN_WORK_QUARTER,
  year: SECONDS_IN_WORK_YEAR,
} as const;

const UNIT_ORDER = [
  ['year', 'y'],
  ['quarter', 'q'],
  ['month', 'mo'],
  ['week', 'w'],
  ['day', 'd'],
  ['hour', 'h'],
  ['minute', 'm'],
] as const;

const UNIT_ALIASES: Record<string, keyof typeof UNITS_TO_SECONDS> = {
  y: 'year',
  q: 'quarter',
  mo: 'month',
  w: 'week',
  d: 'day',
  h: 'hour',
  m: 'minute',
};

export const PARSABLE_PATTERN = /(?<value>-{0,1}\d+(?:\.\d+)?)\s*(?<unit>y|q|mo|w|d|h|m)\b/gi;
export const PARSABLE_PATTERN_VALIDATION = /(?<value>\d+(?:\.\d+)?)\s*(?<unit>y|q|mo|w|d|h|m)?\b/i;

export const parseDuration = (input: string) => {
  if (!input || typeof input !== 'string') {
    return 0;
  }

  return Math.round(
    Array.from(input.matchAll(PARSABLE_PATTERN)).reduce((total, match) => {
      const { value, unit } = match.groups as { value: string; unit: string };
      const parsedValue = parseFloat(value);
      const normalizedUnit = UNIT_ALIASES[unit];

      if (!parsedValue || Number.isNaN(parsedValue) || parsedValue < 0 || !normalizedUnit) {
        return total;
      }

      return total + parsedValue * UNITS_TO_SECONDS[normalizedUnit];
    }, 0),
  );
};

export const parseDurationInput = (input: string): number =>
  parseDuration(isNumeric(input) ? `${input}h` : input);

export const formatDuration = (totalSeconds: number) => {
  if (totalSeconds < 0) {
    return '';
  }

  const duration = UNIT_ORDER.reduce<{ remaining: number; parts: string[] }>(
    ({ remaining, parts }, [unit, abbr]) => {
      const unitSeconds = UNITS_TO_SECONDS[unit];
      const value = Math.floor(remaining / unitSeconds);

      if (value > 0) {
        parts.push(`${value}${abbr}`);
        remaining -= value * unitSeconds;
      }

      return { remaining, parts };
    },
    { remaining: totalSeconds, parts: [] },
  ).parts.join(' ');

  return duration === '' ? '0' : duration;
};

export const isValidDurationString = (val: string): boolean => {
  const trimmed = val.trim();
  if (trimmed === '') return true;

  const parts = trimmed.split(/\s+/);
  return parts.every((part) => {
    const match = part.match(PARSABLE_PATTERN_VALIDATION);
    return !!(match && match[0] === part);
  });
};
